import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useDeleteBulletinMutation } from "entities/bulletin/mutations/useDeleteBulletinMutation";
import { useUpdateBulletinStatusMutation } from "entities/bulletin/mutations/useUpdateBulletinStatusMutation";
import { Bulletin } from "entities/bulletin/types";
import React, { FC } from "react";
import DeleteConfirmationModal from "shared/components/DeleteConfirmationModal";
import FlexColumn from "shared/components/Layout/components/FlexColumn";
import PageHeading from "shared/components/PageHeading";
import PrimaryButton from "shared/components/PrimaryButton";
import TableWithPagination from "shared/components/TableWithPagination";
import BulletinDetailsDrawer from "./components/BulletinDetailsDrawer";
import { BulletinBoardPageProvider, useBulletinBoardPage } from "./context";
import { getBulletinColumns } from "./helpers";

const BulletinBoardContainer = () => (
  <BulletinBoardPageProvider>
    <BulletinBoard />
  </BulletinBoardPageProvider>
);

const BulletinBoard: FC = () => {
  const {
    data,
    isLoading,
    bulletinBoardDetailsDrawer,
    setSelectedBulletin,
    selectedBulletin,
    openDeleteModal,
    handleDeleteModal,
  } = useBulletinBoardPage();

  const { mutate: updateBulletinStatus, isPending: isPendingStatus } =
    useUpdateBulletinStatusMutation();

  const { mutate: deleteBulletin, isPending: isDeleteLoading } =
    useDeleteBulletinMutation(handleDeleteModal);

  const writeAccess = useRolePermission("bulletinBoard:write");

  const columnsParams = {
    bulletinBoardDetailsDrawer: bulletinBoardDetailsDrawer,
    handleStatus: updateBulletinStatus,
    handleDeleteBulletin: handleDeleteModal,
    setSelectedBulletin: setSelectedBulletin,
    readOnly: !writeAccess.allowed,
  };

  const columns = getBulletinColumns(columnsParams);

  const onDeleteBulletin = () => {
    selectedBulletin && deleteBulletin(selectedBulletin.id);
  };

  return (
    <FlexColumn>
      <PageHeading>
        {writeAccess.allowed && (
          <PrimaryButton
            title="Add new bulletin"
            size="small"
            onClick={() => bulletinBoardDetailsDrawer.openDrawer()}
          />
        )}
      </PageHeading>
      <TableWithPagination<Bulletin>
        data={data}
        loading={isLoading || isDeleteLoading || isPendingStatus}
        columns={columns}
        emptyStateMessage="It looks like there are no bulletins available at the moment. Click the 'Add new bulletin' button to post the first announcement."
        sortingMode="client"
        pageSizeOptions={[25, 50, 100]}
        hideFooter
        onRowClick={(params) =>
          bulletinBoardDetailsDrawer.openDrawer(params.row.id)
        }
      />
      <BulletinDetailsDrawer />
      <DeleteConfirmationModal
        title="Are you sure you want to delete this bulletin?"
        applyButtonTitle="Yes, delete"
        onClose={handleDeleteModal}
        open={openDeleteModal}
        handleSuccess={onDeleteBulletin}
        applyButtonLoading={isDeleteLoading}
      />
    </FlexColumn>
  );
};

export default BulletinBoardContainer;
